/**
 * Keybinding settings screen (#70).
 *
 * Renders every bindable action from the LIVE keymap (derived, not hardcoded —
 * config-authored champions' slots appear automatically), each with its
 * current key and a "click to rebind" button. Arming a row captures the next
 * keydown, normalizes it to a `KeyChord`, and writes the keymap through
 * `useKeymap().setBrowserKeymap` (persist + live refresh).
 *
 * SAFETY: while this screen is mounted, `inputSuppressed` is held true so
 * `InputSurface` does not route keys to the sim — a rebind key press can't also
 * fire a game action. While a row is armed, the capture handler additionally
 * `preventDefault`s so the browser doesn't act on the key either.
 *
 * v1: single bind per action, bare-key capture (modifiers land with #68 — the
 * capture already produces a full `KeyChord`, so that's an enrichment).
 *
 * Rendered both as a standalone route (`#/settings`, out of match) and as an
 * in-match overlay; `onClose` lets the overlay dismiss. When used as a route,
 * `onClose` navigates home.
 *
 * #462: every row carries a hover `title` tooltip (`actionTooltip`, the same
 * plain-`title` pattern already used by the conflict badge/reset/close
 * buttons below). SCOPING DECISION (see the issue for the full writeup):
 * `useStore()`'s `ownChampionId` (falling back to `DEFAULT_HUD_CHAMPION_ID`
 * pre-pick/pre-match, the SAME established pattern `ActionBar`/`OwnFrame`/
 * `InputSurface` already use) is reliably available here in BOTH render
 * contexts — the standalone route and the in-match overlay both sit under
 * the root `<StoreProvider>` (`main.tsx`) — so `castSlot` rows show the
 * REAL bound ability's name + authored description (sourced from
 * `abilityFacts.ts`/`abilityLabels.ts`, the single auto-derived source,
 * #461 — not a second hand-copied table), not a generic "Ability N" tooltip.
 * An empty loadout slot (fewer abilities than slots) still falls back to a
 * generic, honest description.
 */

import { useEffect, useState, type ReactElement } from "react";
import {
  useKeymap,
  actionLabel,
  actionTooltip,
  rebindAction,
  resetAction,
  resetAll,
  findConflicts,
  normalizeChord,
  chordId,
  chordLabel,
  gameActionId,
  DEFAULT_KEYMAP,
  type KeyChord,
} from "../input/keybinding/index.js";
import { useWasdStrafeMode } from "../input/wasdStrafeMode.js";
import { useStore } from "../store/context.js";
import { DEFAULT_HUD_CHAMPION_ID } from "../champions/abilityLabels.js";
import type { WasdStrafeMode } from "../prefs.js";

export interface SettingsScreenProps {
  /** Called when the user dismisses (overlay close / route back). */
  readonly onClose?: () => void;
}

/** Display a chord, or an em-dash placeholder when unbound. */
function chordDisplay(chord: KeyChord | undefined): string {
  return chord === undefined ? "—" : chordLabel(chord);
}

/** #351: the two WASD strafe-axis conventions, in display order. */
const WASD_STRAFE_MODE_OPTIONS: readonly { readonly mode: WasdStrafeMode; readonly label: string }[] = [
  { mode: "facing", label: "Facing-relative (today's default)" },
  { mode: "screen", label: "Screen-relative (d = screen-right, w = screen-up)" },
];

export function SettingsScreen({ onClose }: SettingsScreenProps): ReactElement {
  const { keymap, setBrowserKeymap, setInputSuppressed } = useKeymap();
  const { wasdStrafeMode, setWasdStrafeMode } = useWasdStrafeMode();
  // #462: resolve `castSlot` tooltips against the player's REAL loadout.
  // `useStore` is available in both render contexts this screen mounts in —
  // the standalone `#/settings` route and the in-match overlay (both sit
  // under the root `<StoreProvider>`, see `main.tsx`) — so this is the same
  // established pre-pick/pre-match fallback `ActionBar`/`OwnFrame`/
  // `InputSurface` already use, not new plumbing.
  const { state } = useStore();
  const championId = state.ownChampionId ?? DEFAULT_HUD_CHAMPION_ID;
  // The action id currently armed for capture, or null.
  const [armed, setArmed] = useState<string | null>(null);

  // Hold input suppression for the entire lifetime of this screen so the sim
  // never receives keys while the settings UI is up (in-match safety).
  useEffect(() => {
    setInputSuppressed(true);
    return () => {
      setInputSuppressed(false);
    };
  }, [setInputSuppressed]);

  // Keydown handler: mounted for the whole lifetime of the screen (#242 — a
  // handler that only existed while a row was armed meant Escape had no
  // effect the rest of the time, and the menu had no keyboard close path).
  // Two branches:
  //   - armed: capture the next key as a rebind (Escape cancels the capture
  //     without rebinding, as before).
  //   - not armed: Escape closes the whole overlay via `onClose`. This works
  //     from any focus state within the menu (window-level, capture phase)
  //     and — since `stopPropagation` keeps it from reaching InputSurface's
  //     document-level listener — never also fires the in-match Escape →
  //     clearHostile action while the menu is open (belt-and-suspenders —
  //     `inputSuppressed` already blocks that path too, see the effect above).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (armed !== null) {
        e.preventDefault();
        e.stopPropagation();
        // Escape cancels the capture without rebinding.
        if (e.key === "Escape") {
          setArmed(null);
          return;
        }
        // Ignore lone modifier presses so the user can hold e.g. Shift and
        // THEN press the real key to record a chord (#68). A modifier alone
        // is not a binding.
        if (e.key === "Control" || e.key === "Alt" || e.key === "Shift" || e.key === "Meta") {
          return;
        }
        const chord = normalizeChord(e.key, {
          ctrl: e.ctrlKey,
          alt: e.altKey,
          shift: e.shiftKey,
          meta: e.metaKey,
        });
        const action = keymap.bindings.find((b) => gameActionId(b.action) === armed)?.action;
        if (action !== undefined) {
          setBrowserKeymap(rebindAction(keymap, action, chord));
        }
        setArmed(null);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose?.();
      }
    };
    // Capture phase so we intercept before anything else on the page.
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [armed, keymap, setBrowserKeymap, onClose]);

  const conflicts = findConflicts(keymap);
  const conflictedChordIds = new Set(conflicts.map((c) => c.chordId));

  return (
    <div
      data-testid="settings-screen"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: 24,
        maxWidth: 520,
        // #242: cap the overlay to the viewport so it can never grow taller
        // than the screen — the row list below (not this container) is the
        // part that scrolls, keeping header/footer always reachable.
        maxHeight: "90vh",
        overflow: "hidden",
        margin: "0 auto",
        color: "#dfe6ef",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <h2 style={{ margin: 0 }}>Keybindings</h2>
        <button
          type="button"
          data-testid="settings-close"
          onClick={onClose}
          title="Close (Esc)"
          style={{
            padding: "6px 14px",
            background: "#2a3340",
            color: "#fff",
            border: "1px solid #3a4757",
            borderRadius: 6,
          }}
        >
          ✕ Close
        </button>
      </div>

      {/* #351: WASD strafe-axis convention — a client-persisted preference,
          purely additive (default "facing" preserves today's facing-relative
          strafe). Kept OUTSIDE the scrollable keybinding row-list (its own
          fixed section, like the header). */}
      <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={{ fontSize: 13, color: "#9fb0c3" }}>WASD strafe</span>
        <div
          data-testid="settings-wasd-strafe-mode"
          style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
        >
          {WASD_STRAFE_MODE_OPTIONS.map(({ mode, label }) => {
            const active = wasdStrafeMode === mode;
            return (
              <button
                key={mode}
                type="button"
                data-testid={`settings-wasd-strafe-mode-${mode}`}
                aria-pressed={active}
                onClick={() => setWasdStrafeMode(mode)}
                style={{
                  padding: "6px 12px",
                  background: active ? "#5680d6" : "#22303f",
                  color: "#fff",
                  border: "1px solid #3a4757",
                  borderRadius: 4,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        data-testid="settings-row-list"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          // #242: the body — not the whole overlay — scrolls when the
          // binding list exceeds the available height. `minHeight: 0`
          // overrides the flex-item default of `min-height: auto`, which
          // would otherwise force this column to grow to fit its content
          // and push scrolling out to the (non-scrollable) page instead.
          overflowY: "auto",
          minHeight: 0,
        }}
      >
        {keymap.bindings.map((binding) => {
          const id = gameActionId(binding.action);
          const chord = binding.chords[0];
          const key = chord === undefined ? undefined : chordId(chord);
          const isArmed = armed === id;
          const isConflicted = key !== undefined && conflictedChordIds.has(key);
          return (
            <div
              key={id}
              data-testid={`settings-row-${id}`}
              title={actionTooltip(binding.action, championId)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "8px 12px",
                background: "#161c24",
                border: `1px solid ${isConflicted ? "#c9563f" : "#2a3340"}`,
                borderRadius: 6,
              }}
            >
              <span>{actionLabel(binding.action)}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {isConflicted ? (
                  <span
                    data-testid={`settings-conflict-${id}`}
                    title="This key is bound to more than one action"
                    style={{ color: "#e0916f", fontSize: 12 }}
                  >
                    conflict
                  </span>
                ) : null}
                <button
                  type="button"
                  data-testid={`settings-rebind-${id}`}
                  onClick={() => {
                    setArmed(isArmed ? null : id);
                  }}
                  style={{
                    minWidth: 96,
                    padding: "6px 12px",
                    background: isArmed ? "#5680d6" : "#22303f",
                    color: "#fff",
                    border: "1px solid #3a4757",
                    borderRadius: 4,
                    fontFamily: "monospace",
                  }}
                >
                  {isArmed ? "press a key…" : chordDisplay(chord)}
                </button>
                <button
                  type="button"
                  data-testid={`settings-reset-${id}`}
                  title="Reset to default"
                  onClick={() => {
                    setBrowserKeymap(resetAction(keymap, binding.action, DEFAULT_KEYMAP));
                    setArmed(null);
                  }}
                  style={{
                    padding: "6px 8px",
                    background: "#22303f",
                    color: "#9fb0c3",
                    border: "1px solid #3a4757",
                    borderRadius: 4,
                  }}
                >
                  ↺
                </button>
              </span>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        data-testid="settings-reset-all"
        onClick={() => {
          setBrowserKeymap(resetAll(DEFAULT_KEYMAP));
          setArmed(null);
        }}
        style={{
          alignSelf: "flex-start",
          flexShrink: 0,
          marginTop: 8,
          padding: "6px 14px",
          background: "#2a3340",
          color: "#9fb0c3",
          border: "1px solid #3a4757",
          borderRadius: 6,
        }}
      >
        Reset all to defaults
      </button>
    </div>
  );
}
